import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import type { PublicUser } from '../types/gateway';
import { MentionPopup } from './MentionPopup';
import { VoiceNoteRecorder } from './VoiceNoteRecorder';
import styles from './InputBar.module.css';

interface InputBarProps {
  onSend: (message: string, files?: File[], mentions?: string[]) => void;
  teachMode: boolean;
  onToggleTeachMode: () => void;
  disabled?: boolean;
  /** Roster used by the @-mention picker. Empty array disables the picker. */
  roster?: PublicUser[];
  /** The active user's username — excluded from the picker. */
  currentUsername?: string;
  /** Active thread session key — passed to the voice-note recorder so the
   *  audio is saved as a thread artifact alongside the transcript. */
  activeSessionId?: string | null;
  /** Slash-command intercept hook. Called when the composer message starts
   *  with `/wrong`, `/wrong:`, `/feedback`, or `/feedback:`. App-level handler
   *  POSTs to /api/auth/feedback/report and returns true on success. The
   *  command is NEVER forwarded to the agent — Foreman would otherwise try to
   *  "answer" the complaint. Returns false if no recent Foreman turn exists
   *  on the thread (the slash command needs a target message). */
  onFeedbackCommand?: (reason: string) => Promise<{ ok: boolean; error?: string }>;
}

/** True iff `text` is a feedback slash command. Matches /wrong, /wrong:,
 *  /wrong <args>, /feedback, /feedback:, /feedback <args>. Case-insensitive.
 *  Exported for unit-testability. */
export function parseFeedbackCommand(text: string): { isCommand: boolean; reason: string } {
  const m = text.match(/^\s*\/(wrong|feedback)\b[:\s]*(.*)$/is);
  if (!m) return { isCommand: false, reason: '' };
  return { isCommand: true, reason: (m[2] || '').trim() };
}

/** Extract `@username` mentions from message text. Matches usernames anchored
 *  at start-of-string or after whitespace. */
export function extractMentions(text: string, validUsernames: Set<string>): string[] {
  const found = new Set<string>();
  const re = /(^|\s)@([a-z0-9_-]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const name = match[2].toLowerCase();
    if (validUsernames.has(name)) found.add(name);
  }
  return Array.from(found);
}

export function InputBar({
  onSend,
  teachMode,
  onToggleTeachMode,
  disabled,
  roster = [],
  currentUsername,
  activeSessionId,
  onFeedbackCommand,
}: InputBarProps) {
  const [text, setText] = useState('');
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  /** Inline confirmation banner for slash-command feedback. Auto-clears in 3s.
   *  Kept tiny + ephemeral on purpose: this isn't a toast system, just the
   *  bare minimum to confirm the flag landed without ripping the user out of
   *  the composer. */
  const [feedbackFlash, setFeedbackFlash] = useState<null | { kind: 'sent' | 'error'; msg: string }>(null);
  const feedbackFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (feedbackFlashTimer.current) clearTimeout(feedbackFlashTimer.current);
    },
    []
  );

  const showFeedbackFlash = (kind: 'sent' | 'error', msg: string) => {
    setFeedbackFlash({ kind, msg });
    if (feedbackFlashTimer.current) clearTimeout(feedbackFlashTimer.current);
    feedbackFlashTimer.current = setTimeout(() => setFeedbackFlash(null), 3000);
  };

  // ── Mention picker state ──
  const [mentionQuery, setMentionQuery] = useState<string | null>(null); // null = picker closed
  const [mentionStart, setMentionStart] = useState<number>(-1);
  const [highlight, setHighlight] = useState(0);
  const [popupAnchor, setPopupAnchor] = useState<{ left: number; top: number } | null>(null);

  const candidates = (() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return roster
      .filter((u) => u.username !== currentUsername)
      .filter((u) =>
        q === '' ||
        u.username.toLowerCase().includes(q) ||
        u.name.toLowerCase().includes(q)
      )
      .slice(0, 6);
  })();

  // Track @-mention trigger in textarea
  const checkForMention = (value: string, caret: number) => {
    // Find `@` looking back from caret. Must be at start or after whitespace.
    let i = caret - 1;
    while (i >= 0) {
      const ch = value[i];
      if (ch === '@') {
        const prev = i > 0 ? value[i - 1] : ' ';
        if (i === 0 || /\s/.test(prev)) {
          const query = value.slice(i + 1, caret);
          // Stop if query contains whitespace (closes the popup naturally)
          if (/\s/.test(query)) {
            setMentionQuery(null);
            return;
          }
          setMentionQuery(query);
          setMentionStart(i);
          setHighlight(0);
          // Anchor popup to the textarea's bottom-left for simplicity.
          if (textareaRef.current) {
            const r = textareaRef.current.getBoundingClientRect();
            setPopupAnchor({ left: r.left + 12, top: r.top });
          }
          return;
        } else {
          break;
        }
      }
      if (/\s/.test(ch)) break;
      i--;
    }
    setMentionQuery(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setText(value);
    checkForMention(value, e.target.selectionStart);
  };

  const handleSelect = (user: PublicUser) => {
    if (mentionStart < 0) return;
    const before = text.slice(0, mentionStart);
    const after = text.slice(textareaRef.current?.selectionStart ?? mentionStart);
    const insertion = `@${user.username} `;
    const next = before + insertion + after;
    setText(next);
    setMentionQuery(null);
    // Restore caret position
    setTimeout(() => {
      if (textareaRef.current) {
        const pos = before.length + insertion.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  };

  const handleSend = () => {
    const hasText = text.trim().length > 0;
    const hasFiles = stagedFiles.length > 0;
    if ((!hasText && !hasFiles) || disabled) return;

    // ── Slash-command intercept (/wrong:, /feedback:) ────────────────────────
    // Runs BEFORE the gateway send so Foreman never sees the raw command. If
    // the user typed just "/wrong" with no reason, prompt them to add one
    // rather than silently dropping the turn. If onFeedbackCommand isn't
    // wired up (e.g. on a thread with no active session), fall through to a
    // normal send — better to send the literal command than lose the text.
    const cmd = parseFeedbackCommand(text);
    if (cmd.isCommand && onFeedbackCommand) {
      if (cmd.reason.length < 5) {
        showFeedbackFlash(
          'error',
          'Add a short reason after /wrong: (at least 5 chars).'
        );
        // Leave the text in the composer so the user can append their reason.
        return;
      }
      // Fire-and-forget — keep the UI responsive. We clear the composer
      // immediately on optimistic assumption of success and rewind on error.
      const captured = text;
      setText('');
      setStagedFiles([]);
      setMentionQuery(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onFeedbackCommand(cmd.reason)
        .then((res) => {
          if (res.ok) {
            showFeedbackFlash('sent', '✓ Flagged. Martin sees it.');
          } else {
            // Restore the composer so the user can retry without retyping.
            setText(captured);
            showFeedbackFlash(
              'error',
              res.error || "Couldn't flag — try again."
            );
          }
        })
        .catch(() => {
          setText(captured);
          showFeedbackFlash('error', "Couldn't flag — try again.");
        });
      return;
    }

    const validUsernames = new Set(roster.map((u) => u.username));
    const mentions = extractMentions(text, validUsernames);

    onSend(
      text || (hasFiles ? `Audit these ${stagedFiles.length} file(s)` : ''),
      hasFiles ? stagedFiles : undefined,
      mentions
    );
    setText('');
    setStagedFiles([]);
    setMentionQuery(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Mention picker takes precedence over send/newline
    if (mentionQuery !== null && candidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => (h + 1) % candidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => (h - 1 + candidates.length) % candidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        handleSelect(candidates[highlight]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea up to 6 lines
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 144) + 'px';
  }, [text]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setStagedFiles((prev) => [...prev, ...Array.from(files)]);
      e.target.value = '';
    }
  };

  const removeFile = (index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /** Append a voice-note transcript to the composer rather than overwrite —
   *  the user may have already typed context before hitting record. */
  const handleTranscript = useCallback((transcript: string) => {
    setText((prev) => (prev ? `${prev}\n\n${transcript}` : transcript));
    // Refocus the textarea so the user can immediately edit/send.
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  }, []);

  const fileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return '\u{1F4C4}';
    if (ext === 'xlsx' || ext === 'csv') return '\u{1F4CA}';
    if (ext === 'docx') return '\u{1F4DD}';
    if (['jpg', 'jpeg', 'png', 'heic'].includes(ext || '')) return '\u{1F5BC}';
    return '\u{1F4CE}';
  };

  return (
    <div className={styles.inputBar}>
      {teachMode && (
        <div className={styles.teachBanner}>
          Teach Mode: ON — Everything you say will be captured as KBW knowledge.
        </div>
      )}
      {feedbackFlash && (
        <div
          className={
            feedbackFlash.kind === 'sent'
              ? styles.feedbackFlashOk
              : styles.feedbackFlashErr
          }
          role="status"
        >
          {feedbackFlash.msg}
        </div>
      )}

      {stagedFiles.length > 0 && (
        <div className={styles.fileChips}>
          {stagedFiles.map((f, i) => (
            <div key={`${f.name}-${i}`} className={styles.fileChip}>
              <span className={styles.fileIcon}>{fileIcon(f.name)}</span>
              <span className={styles.fileName}>{f.name}</span>
              <span className={styles.fileSize}>
                {f.size < 1024 * 1024
                  ? `${Math.round(f.size / 1024)}KB`
                  : `${(f.size / (1024 * 1024)).toFixed(1)}MB`}
              </span>
              <button
                className={styles.fileRemove}
                onClick={() => removeFile(i)}
                title="Remove file"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      <div className={styles.inputRow}>
        <button
          className={styles.attachBtn}
          onClick={() => fileInputRef.current?.click()}
          title="Attach file"
        >
          &#x1F4CE;
        </button>
        <input
          ref={fileInputRef}
          type="file"
          className={styles.fileInput}
          onChange={handleFileChange}
          accept=".xml,.dxf,.jpg,.jpeg,.png,.heic,.csv,.xlsx,.pdf,.docx"
          multiple
        />
        <textarea
          ref={textareaRef}
          className={styles.textInput}
          placeholder={
            stagedFiles.length > 0
              ? `${stagedFiles.length} file(s) ready — type a message or hit Send`
              : 'Holler at Foreman... (type @ to mention a teammate)'
          }
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => setMentionQuery(null), 150)}
          disabled={disabled}
          rows={1}
        />
        <VoiceNoteRecorder
          activeSessionId={activeSessionId ?? null}
          onTranscript={handleTranscript}
          disabled={disabled}
        />
        <button
          className={`${styles.teachBtn} ${teachMode ? styles.teachActive : ''}`}
          onClick={onToggleTeachMode}
          title="Toggle Teach Mode"
        >
          &#x1F393;
        </button>
        <button
          className={`${styles.sendBtn} ${(text.trim() || stagedFiles.length > 0) ? styles.sendActive : ''}`}
          onClick={handleSend}
          disabled={(!text.trim() && stagedFiles.length === 0) || disabled}
        >
          Send
        </button>
      </div>

      <MentionPopup
        candidates={candidates}
        highlightIndex={highlight}
        anchor={popupAnchor}
        onSelect={handleSelect}
        onHover={setHighlight}
      />
    </div>
  );
}
